import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Money } from '@clari/ui'
import { ErrorBanner } from '@/components/ErrorBanner'
import { apiErrorMessage } from '@/lib/api-error'
import { adminApi } from '@/lib/admin-api'
import { formatDate } from '@/lib/format'

const TX_STATUSES = ['pending', 'booked', 'rejected', 'canceled']

export function Transactions() {
  const { t, i18n } = useTranslation('admin')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)

  const query = useQuery({
    queryKey: ['admin-transactions', search, status, page],
    queryFn: () =>
      adminApi.listTransactions({
        ...(search ? { search } : {}),
        ...(status ? { status } : {}),
        page,
      }),
  })
  const totalPages = query.data ? Math.max(1, Math.ceil(query.data.total / query.data.pageSize)) : 1

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-semibold">{t('transactions.title')}</h1>

      <div className="flex flex-wrap items-end gap-3">
        <input
          type="search"
          className="clari-input max-w-xs"
          placeholder={t('transactions.search_placeholder')}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPage(1)
          }}
          aria-label={t('transactions.search_placeholder')}
        />
        <select
          className="clari-input max-w-xs"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value)
            setPage(1)
          }}
          aria-label={t('transactions.status_filter')}
        >
          <option value="">{t('common.all')}</option>
          {TX_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {query.isError ? <ErrorBanner message={apiErrorMessage(query.error)} /> : null}
      {query.isLoading ? <p className="text-text-muted">{t('common.loading')}</p> : null}

      {query.data ? (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border text-text-muted">
              <tr>
                <th className="px-4 py-2 font-medium">{t('transactions.col_owner')}</th>
                <th className="px-4 py-2 font-medium">{t('transactions.col_type')}</th>
                <th className="px-4 py-2 font-medium">{t('transactions.col_amount')}</th>
                <th className="px-4 py-2 font-medium">{t('transactions.col_status')}</th>
                <th className="px-4 py-2 font-medium">{t('transactions.col_date')}</th>
              </tr>
            </thead>
            <tbody>
              {query.data.items.map((tx) => (
                <tr key={tx.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-2 font-mono">{tx.owner.email}</td>
                  <td className="px-4 py-2">{tx.type}</td>
                  <td className="px-4 py-2">
                    <Money
                      amountCents={tx.amount_cents}
                      currency={tx.currency}
                      locale={i18n.language}
                      showSign
                    />
                  </td>
                  <td className="px-4 py-2">{tx.status}</td>
                  <td className="px-4 py-2 text-text-muted">
                    {formatDate(tx.booked_at ?? tx.created_at, i18n.language)}
                  </td>
                </tr>
              ))}
              {query.data.items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-text-muted">
                    {t('transactions.empty')}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="flex items-center gap-3 text-sm">
        <button
          type="button"
          className="text-text-muted hover:text-text disabled:opacity-40"
          disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          {t('common.prev')}
        </button>
        <span className="text-text-muted">{t('common.page', { page })}</span>
        <button
          type="button"
          className="text-text-muted hover:text-text disabled:opacity-40"
          disabled={page >= totalPages}
          onClick={() => setPage((p) => p + 1)}
        >
          {t('common.next')}
        </button>
      </div>
    </div>
  )
}
